import Button from './Button';
import { isPhoneExperience } from '../../navigation/phoneExperience';
import PhoneLoadingScreen from '../mobile/PhoneLoadingScreen';

/**
 * AppLoadingScreen — full-screen branded gate shown ONLY during the
 * initial app bootstrap (auth resolution + first data load).
 *
 * After bootstrap completes this screen should never appear again.
 * Subsequent loading states use GlobalProgressBar + skeleton loaders instead.
 */
const AppLoadingScreen = ({
  title = 'Getting Vylo ready',
  subtitle = null,
  onRetry,
}) => {
  if (isPhoneExperience()) return <PhoneLoadingScreen title={title} subtitle={subtitle} onRetry={onRetry} />;
  const titleId = 'app-boot-loader-title';
  const subtitleId = subtitle ? 'app-boot-loader-subtitle' : undefined;

  return (
    <div
      data-testid="app-boot-loader"
      data-loading-scope="application-bootstrap"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        background: 'var(--color-bg)',
      }}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-busy={!onRetry}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={subtitleId}
      aria-label={title ? undefined : 'Getting Vylo ready'}
    >
    <style>{`
      @keyframes app-loading-bar-pulse {
        0% { transform: translateX(-35%) scaleX(0.75); opacity: 0.45; }
        50% { transform: translateX(35%) scaleX(1); opacity: 1; }
        100% { transform: translateX(165%) scaleX(0.8); opacity: 0.45; }
      }
      @keyframes app-loading-logo-pulse {
        0%, 100% { transform: scale(0.985); opacity: 0.94; }
        50% { transform: scale(1.015); opacity: 1; }
      }
      @keyframes app-loading-dot {
        0%, 80%, 100% { transform: translateY(0) scale(0.86); opacity: 0.42; }
        40% { transform: translateY(-3px) scale(1); opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        [data-testid="app-boot-loader"] [data-loader-animated="true"] {
          animation: none !important;
        }
      }
    `}</style>

    <div aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] overflow-hidden bg-[var(--color-surface-hover)] ${onRetry ? 'hidden' : ''}`}>
      <div
        data-loader-animated="true"
        className="h-full w-[34%] rounded-r-full"
        style={{
          background: 'var(--color-accent)',
          boxShadow: 'none',
          animation: 'app-loading-bar-pulse 1.65s ease-in-out infinite',
          transformOrigin: 'left center'
        }}
      />
    </div>

    {/* Logo tile */}
    <div className="relative">
      <div
        data-loader-animated="true"
        className="relative flex h-[86px] w-[86px] overflow-hidden rounded-[24px]"
        style={{
          boxShadow: '0 0 0 1px var(--color-border)',
          animation: onRetry ? 'none' : 'app-loading-logo-pulse 1.9s ease-in-out infinite'
        }}
      >
        <img
          src="/icons/apple-touch-icon.png"
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
      </div>
    </div>

    <div className={`mt-7 flex items-center gap-2 ${onRetry ? 'invisible' : ''}`} aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          data-loader-animated="true"
          className="h-2 w-2 rounded-full bg-[var(--color-accent)]"
          style={{ animation: `app-loading-dot 1.2s ease-in-out ${index * 0.14}s infinite` }}
        />
      ))}
    </div>

    {title ? (
      <div className="mt-5 max-w-md px-6 text-center">
        <p id={titleId} className="text-[1.35rem] font-semibold tracking-[-0.02em] text-[var(--color-text-primary)]">
          {title}
        </p>
        {subtitle ? (
          <p id={subtitleId} className="mt-2 text-sm leading-6 text-[var(--color-text-secondary)]">
            {subtitle}
          </p>
        ) : null}
      </div>
    ) : null}
    {onRetry ? (
      <Button type="button" onClick={onRetry} className="mt-5 px-6 py-3">
        Try again
      </Button>
    ) : null}
    </div>
  );
};

export default AppLoadingScreen;
